import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase'; // Import your firebase config
import CarCard from '../components/CarCard';
import heroBgVideo from '../assets/hero-video.mp4'; 

// --- Reusable Component for Scroll Animations ---
const AnimatedSection = ({ children, direction = 'up', delay = 0, style = {} }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target); 
        }
      });
    }, { threshold: 0.1 }); 

    const currentRef = domRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  let initialTransform = 'translateY(30px)';
  if (direction === 'left') initialTransform = 'translateX(-50px)';
  if (direction === 'right') initialTransform = 'translateX(50px)';
  if (direction === 'none') initialTransform = 'scale(0.95)';

  return (
    <div
      ref={domRef}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate(0, 0) scale(1)' : initialTransform,
        transition: `opacity 0.8s ease-out ${delay}s, transform 0.8s ease-out ${delay}s`,
        ...style
      }}
    >
      {children}
    </div>
  );
};


const Home = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  
  // --- Data State ---
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Search State ---
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [searchError, setSearchError] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  // --- Fetch Data from Firebase ---
  useEffect(() => {
    const fetchCars = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "cars"));
        const carsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCars(carsData);
      } catch (error) {
        console.error("Error fetching cars:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCars();
  }, []);

  // Get unique makes from fetched data
  const uniqueMakes = [...new Set(cars.map(car => car.make))];

  // Models filtered by selected make
  const availableModels = selectedMake
    ? [...new Set(cars.filter(car => car.make === selectedMake).map(car => car.model))]
    : [];

  const handleMakeChange = (e) => {
    setSelectedMake(e.target.value);
    setSelectedModel('');
    setSearchError('');
    setSearchResults(null); 
  };

  const handleSearch = () => {
    const matchingCars = cars.filter((car) => {
      const matchMake = selectedMake ? car.make === selectedMake : true;
      const matchModel = selectedModel ? car.model === selectedModel : true;
      const matchPrice = maxPrice ? car.price <= parseInt(maxPrice) : true;
      return matchMake && matchModel && matchPrice;
    });

    if (matchingCars.length > 0) {
      setSearchError('');
      setSearchResults(matchingCars);
      
      const resultsSection = document.getElementById('featured-section');
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      setSearchError("Car not available now");
      setSearchResults([]); 
    }
  };

  const clearSearch = () => {
    setSearchResults(null);
    setSelectedMake('');
    setSelectedModel('');
    setMaxPrice('');
    setSearchError('');
  };

  const handleVideoLoop = () => {
    const video = videoRef.current;
    if (!video) return;
    const buffer = 1.5; 
    const timeLeft = video.duration - video.currentTime;
    if (timeLeft < buffer) video.style.opacity = 0;
    else if (video.currentTime < buffer) video.style.opacity = 1;
  };

  // Display cars logic
  const displayCars = searchResults ? searchResults : cars.slice(0, 3);

  return (
    <div>
      {/* Hero Section */}
      <header
        style={{
          position: 'relative',
          minHeight: '85vh', 
          display: 'flex',
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
          overflow: 'hidden',
          backgroundColor: '#051337',
          paddingTop: '80px', 
          paddingBottom: '40px',
          boxSizing: 'border-box'
        }}
      >
        <video
          ref={videoRef}
          onTimeUpdate={handleVideoLoop}
          autoPlay loop muted playsInline
          style={{
            position: 'absolute',
            width: '100%', height: '100%',
            objectFit: 'cover',
            top: 0, left: 0, zIndex: 0,
            transition: 'opacity 1.5s ease-in-out',
            opacity: 1,
            pointerEvents: 'none' // ADDED: Prevents play button/interaction
          }}
        >
          <source src={heroBgVideo} type="video/mp4" />
        </video>

        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(5, 19, 55, 0.7)', zIndex: 1 }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 2, padding: '20px' }}>
          
          <AnimatedSection direction="up" delay={0.2}>
            <h1 style={{ 
                fontSize: 'clamp(2rem, 5vw, 3.5rem)', 
                marginBottom: '20px', 
                fontWeight: 'bold', 
                color: '#ffffff',
                textShadow: '3px 3px 6px rgba(5, 19, 55, 1)' 
            }}>
                Cremlora LLC: Driven by Trust.
            </h1>
            <p style={{ fontSize: '1.2rem', marginBottom: '40px', maxWidth: '600px', margin: '0 auto 40px', lineHeight: '1.6', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                Professional. Transparent. Efficient. We protect your investment with certified quality vehicles.
            </p>
          </AnimatedSection>

          <AnimatedSection direction="up" delay={0.4}>
            <div
                style={{
                background: 'rgba(255,255,255,0.1)',
                padding: '25px',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
                maxWidth: '900px',
                margin: '0 auto',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
                }}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px' }}>
                    <h3 style={{ color: 'white', textAlign: 'left', marginLeft: '10px', margin: '0' }}>
                    Buy/Sell Your Next Car
                    </h3>
                    <button 
                        onClick={() => navigate('/contact', { state: { selectedSubject: 'I want to sell my car' } })}
                        style={{
                            background: '#28a745', 
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '6px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                            transition: 'transform 0.2s, background 0.2s',
                            marginRight: '10px'
                        }}
                        onMouseOver={(e) => { e.target.style.transform = 'scale(1.05)'; e.target.style.background = '#218838'; }}
                        onMouseOut={(e) => { e.target.style.transform = 'scale(1)'; e.target.style.background = '#28a745'; }}
                    >
                        Click Here To Sell Your Car
                    </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                <select value={selectedMake} onChange={handleMakeChange} style={{ padding: '12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>
                    <option value="">All Makes</option>
                    {uniqueMakes.map(make => <option key={make} value={make}>{make}</option>)}
                </select>

                <select 
                    value={selectedModel} 
                    onChange={e => { setSelectedModel(e.target.value); setSearchError(''); }} 
                    disabled={!selectedMake}
                    style={{ padding: '12px', borderRadius: '6px', border: 'none', cursor: !selectedMake ? 'not-allowed' : 'pointer', opacity: !selectedMake ? 0.7 : 1 }}
                >
                    <option value="">{selectedMake ? 'All Models' : 'Select Make First'}</option>
                    {availableModels.map(model => <option key={model} value={model}>{model}</option>)}
                </select>

                <button
                    onClick={handleSearch}
                    className="btn btn-primary"
                    style={{ textAlign: 'center', display: 'block', width: '100%', padding: '12px', background: '#007bff', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem', transition: 'background 0.3s' }}
                    onMouseOver={(e) => e.target.style.background = '#0056b3'}
                    onMouseOut={(e) => e.target.style.background = '#007bff'}
                >
                    SEARCH DEALERSHIP
                </button>
                </div>
                
                {searchError && (
                    <div style={{ color: '#ff6b6b', fontWeight: 'bold', marginTop: '15px', background: 'rgba(0,0,0,0.6)', padding: '10px', borderRadius: '6px' }}>
                        ⚠️ {searchError}
                    </div>
                )}
            </div>
          </AnimatedSection>
        </div>
      </header>

      {/* Featured / Search Results Inventory */}
      <section id="featured-section" className="container" style={{ padding: '80px 20px' }}>
        <AnimatedSection direction="up">
            <div style={{display:'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginBottom: '50px'}}>
                <h2 style={{ textAlign: 'center', fontSize: '2.5rem', color: '#333', margin: 0 }}>
                {searchResults ? 'Search Results' : 'Featured Dealership Items'}
                </h2>
                {searchResults && (
                    <button 
                        onClick={clearSearch} 
                        className="btn btn-outline"
                        style={{ fontSize: '0.9rem', padding: '8px 16px' }}
                    >
                        Clear Search
                    </button>
                )}
            </div>
        </AnimatedSection>

        {loading ? (
           <div style={{textAlign: 'center', padding: '40px'}}>Loading vehicles...</div>
        ) : displayCars.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px', justifyItems: 'center', alignItems: 'center' }}>
            {displayCars.map((car, index) => (
                <AnimatedSection key={car.id} delay={index * 0.2} style={{ width: '100%' }}>
                    <CarCard car={car} />
                </AnimatedSection>
            ))}
            </div>
        ) : (
            <div style={{ textAlign: 'center', padding: '40px', background: '#f4f4f4', borderRadius: '10px' }}>
                <h3>No vehicles found.</h3>
                <p>Try adjusting your search criteria.</p>
            </div>
        )}

        <AnimatedSection direction="up" delay={0.3} style={{ textAlign: 'center', marginTop: '50px' }}>
          <button
            onClick={() => navigate('/inventory')}
            className="btn btn-outline"
            style={{ padding: '12px 30px', border: '2px solid #007bff', color: '#007bff', background: 'transparent', textDecoration: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: '1.1rem', cursor: 'pointer', transition: 'all 0.3s ease' }}
            onMouseOver={(e) => { e.target.style.background = '#007bff'; e.target.style.color = 'white'; }}
            onMouseOut={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#007bff'; }}
          >
            View Full Dealership
          </button>
        </AnimatedSection>
      </section>

      {/* NEW SECTION: Business Operations Information */}
      <section style={{ background: '#eef1f5', padding: '60px 20px', borderTop: '1px solid #ddd', borderBottom: '1px solid #ddd' }}>
        <div className="container" style={{ maxWidth: '1100px' }}>
            <AnimatedSection direction="up">
                <h2 style={{ textAlign: 'center', marginBottom: '40px', fontSize: '2.2rem' }}>Our Business Operations</h2>
            </AnimatedSection>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
                
                {/* 1. Vehicle Dealership */}
                <AnimatedSection delay={0.1}>
                    <div style={{ background: 'white', padding: '25px', borderRadius: '8px', height: '100%', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                        <h3 style={{ marginBottom: '15px', color: '#0188ce' }}>Vehicle Dealership</h3>
                        <ul style={{ listStyleType: 'disc', paddingLeft: '20px', color: '#555', lineHeight: '1.7' }}>
                            <li><strong>Vehicle Sourcing & Inspection:</strong> Vehicles are acquired through private sellers, auctions, and trade-ins, and inspected for condition, safety, and title validity.</li>
                            <li><strong>Pricing & Sales:</strong> Vehicles are competitively priced, clearly listed, and sold through secure payments and available financing options.</li>
                            <li><strong>Delivery & Documentation:</strong> We manage title transfer, registration support, and vehicle delivery.</li>
                            <li><strong>Customer Support:</strong> End-to-end assistance before, during, and after the sale.</li>
                        </ul>
                    </div>
                </AnimatedSection>

                {/* 2. Electronics */}
                <AnimatedSection delay={0.2}>
                    <div style={{ background: 'white', padding: '25px', borderRadius: '8px', height: '100%', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                        <h3 style={{ marginBottom: '15px', color: '#0188ce' }}>Electronics</h3>
                        <ul style={{ listStyleType: 'disc', paddingLeft: '20px', color: '#555', lineHeight: '1.7' }}>
                            <li><strong>Sourcing & Testing:</strong> Electronics are sourced from verified sellers and tested for functionality and authenticity.</li>
                            <li><strong>Inventory & Listings:</strong> Products are accurately listed, competitively priced, and tracked in real time.</li>
                            <li><strong>Sales & Fulfillment:</strong> Secure checkout, fast shipping, and clear return policies.</li>
                            <li><strong>Customer Support:</strong> Responsive support for product and order inquiries.</li>
                        </ul>
                    </div>
                </AnimatedSection>

                {/* 3. General Merchandise */}
                <AnimatedSection delay={0.3}>
                    <div style={{ background: 'white', padding: '25px', borderRadius: '8px', height: '100%', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                        <h3 style={{ marginBottom: '15px', color: '#0188ce' }}>General Merchandise</h3>
                        <ul style={{ listStyleType: 'disc', paddingLeft: '20px', color: '#555', lineHeight: '1.7' }}>
                            <li><strong>Product Acquisition:</strong> Goods are sourced from individuals, wholesalers, and approved vendors.</li>
                            <li><strong>Inspection & Listing:</strong> Items are inspected, accurately described, and competitively priced.</li>
                            <li><strong>Sales & Fulfillment:</strong> Secure transactions with prompt packaging and delivery.</li>
                            <li><strong>Customer Service & Compliance:</strong> Clear policies, dedicated support, and regulatory compliance.</li>
                        </ul>
                    </div>
                </AnimatedSection>
            </div>
        </div>
      </section>

      {/* Value Props */}
      <section style={{ background: '#051337', color: 'white', padding: '80px 20px' }}>
        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '40px', textAlign: 'center', maxWidth: '1200px', margin: '0 auto' }}>
          
          <AnimatedSection delay={0.1}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>Certified Quality</h3>
            <p style={{ opacity: 0.8, lineHeight: '1.6' }}>Every vehicle passes a rigorous 150-point inspection before it hits our lot.</p>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>Transparent Pricing</h3>
            <p style={{ opacity: 0.8, lineHeight: '1.6' }}>No hidden fees. No surprise charges. What you see is exactly what you pay.</p>
          </AnimatedSection>

          <AnimatedSection delay={0.5}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>Warranty Included</h3>
            <p style={{ opacity: 0.8, lineHeight: '1.6' }}>Drive with peace of mind. We protect your road ahead with our standard warranty.</p>
          </AnimatedSection>

        </div>
      </section>

      {/* Why Cremlora LLC Section */}
      <section className="container" style={{ padding: '80px 20px', maxWidth: '1000px' }}>
        <AnimatedSection direction="left">
            <h2 style={{ textAlign: 'center', fontSize: '2.2rem', marginBottom: '30px', color: '#051337' }}>
            Why Choose Cremlora LLC as Your Dealership?
            </h2>
            <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#555', textAlign: 'left' }}>
            At Cremlora LLC, we redefine the car buying and selling experience by combining transparency, quality, and customer-focused service. Every vehicle on our lot is carefully inspected to meet high safety and performance standards, ensuring you drive away with confidence. We offer a wide selection of reliable cars, trucks, and SUVs—ranging from budget-friendly options to premium models—so you can find the perfect vehicle for your lifestyle and budget.
            </p>
            <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#555', marginTop: '20px', textAlign: 'left' }}>
            Our team is committed to honesty, efficiency, and exceptional support at every step. From upfront pricing to fast approvals and flexible financing options, Cremlora LLC makes the purchase and sale process simple and stress-free. When you choose us, you’re not just buying or selling a car—you’re partnering with a dealership that values integrity, long-term customer relationships, and a seamless ownership experience.
            </p>
        </AnimatedSection>
      </section>

      {/* Payment Options & Financing Section */}
      <section style={{ background: '#f4f4f4', padding: '5px 20px' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          
          <AnimatedSection direction="up">
            <h2 style={{ textAlign: 'center', fontSize: '2.2rem', marginBottom: '40px', color: '#051337', marginTop: '5px' }}>
                PAYMENT OPTIONS
            </h2>

            <div style={{ marginBottom: '40px' }}>
                <p style={{ fontSize: '1.1rem', marginBottom: '20px' }}>
                Cremlora LLC provides multiple secure payment methods to make your purchase convenient:
                </p>
                <ul style={{ listStyleType: 'disc', paddingLeft: '20px', fontSize: '1.1rem', lineHeight: '1.8' }}>
                <li>Cash payments</li>
                <li>Certified bank checks</li>
                <li>Debit/Credit card payments (limits may apply)</li>
                <li>Online or mobile payment options (if applicable)</li>
                </ul>
                <p style={{ fontSize: '1rem', marginTop: '10px', fontStyle: 'italic', color: '#666' }}>
                All payments are processed securely, and customers receive full documentation for the transaction.
                </p>
            </div>
          </AnimatedSection>

          <AnimatedSection direction="right" delay={0.2}>
            <div style={{ marginBottom: '40px' }}>
                <h3 style={{ fontSize: '1.8rem', marginBottom: '20px', color: '#051337' }}>Financing Process</h3>
                <p style={{ fontSize: '1.1rem', marginBottom: '20px' }}>
                Cremlora LLC offers easy, dealership-assisted financing designed for all credit levels. The financing process typically includes:
                </p>
                <ol style={{ listStyleType: 'decimal', paddingLeft: '20px', fontSize: '1.1rem', lineHeight: '1.8' }}>
                <li style={{ marginBottom: '10px' }}><strong>Application Submission:</strong> Customers complete a simple credit application online or in person.</li>
                <li style={{ marginBottom: '10px' }}><strong>Credit Evaluation:</strong> Our financing partners review your credit history, income, employment status, and debt-to-income ratio.</li>
                <li style={{ marginBottom: '10px' }}><strong>Loan Offer Presentation:</strong> You receive available loan terms—including interest rates, down payment amounts, and monthly payment options.</li>
                <li style={{ marginBottom: '10px' }}><strong>Vehicle Selection & Approval:</strong> Once approved, you select your vehicle, sign your financing documents, and drive away.</li>
                </ol>
                <p style={{ fontSize: '1.1rem', marginTop: '20px' }}>
                Cremlora LLC works with multiple banks and lenders to help customers get competitive rates and flexible terms.
                </p>
            </div>
          </AnimatedSection>

          <AnimatedSection direction="left" delay={0.2}>
            <div style={{marginBottom: '60px'}}>
                <h3 style={{ fontSize: '1.8rem', marginBottom: '20px', color: '#051337' }}>Credit Requirements</h3>
                <p style={{ fontSize: '1.1rem', marginBottom: '20px' }}>
                We welcome all buyers and offer financing solutions for many credit situations:
                </p>
                <ul style={{ listStyleType: 'disc', paddingLeft: '20px', fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '30px' }}>
                <li style={{ marginBottom: '10px' }}><strong>Excellent Credit:</strong> Qualified buyers may receive the lowest interest rates and minimal down payment requirements.</li>
                <li style={{ marginBottom: '10px' }}><strong>Average Credit:</strong> Buyers may receive standard rates with reasonable down payments.</li>
                <li style={{ marginBottom: '10px' }}><strong>Low or No Credit:</strong> Programs may be available with higher down payments, proof of income, and verified employment.</li>
                </ul>

                <h4 style={{ fontSize: '1.3rem', marginBottom: '15px', color: '#333' }}>Common requirements include:</h4>
                <ul style={{ listStyleType: 'circle', paddingLeft: '20px', fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '30px' }}>
                <li>Valid driver’s license</li>
                <li>Proof of income (pay stubs, bank statements, tax returns, etc.)</li>
                <li>Proof of residence</li>
                <li>Down payment (amount varies by credit profile)</li>
                <li>Social Security number for credit checks</li>
                </ul>
                <p style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#051337' }}>
                Cremlora LLC strives to help every customer find a financing option that fits their financial situation while ensuring affordability and long-term success.
                </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

    </div>
  );
};

export default Home;